import type { Tracker } from "../tracking";
import type { ProductContext, RemoteAssistantConfig } from "../types";
import {
  type AssistantAction,
  type AssistantFailure,
  type AssistantImageContext,
  type AssistantProductCard,
  type AssistantProductPrice,
  type AssistantQuoteItem,
  type AssistantReply,
  buildAssistantBody,
  fetchAssistantPrices,
  loadAssistantSession,
  searchByImage,
  sendAssistantFeedback,
  sendAssistantMessage,
} from "./api";
import {
  type ResolvedAppearance,
  contrastColor,
  panelBottomPx,
  panelOffsetCss,
  resolveAppearance,
  toHttpsUrl,
} from "./appearance";
import {
  type StoredAssistantConversation,
  forgetAssistantConversation,
  readStoredAssistantHistory,
  rememberAssistantConversation,
} from "./history";
import { iconNode } from "./icons";
import { captionFrom, validateImageFile } from "./image";
import {
  type AssistantSession,
  isActive,
  readPendingClientSessionId,
  readStoredSession,
  removePendingClientSessionId,
  removeStoredSession,
  writePendingClientSessionId,
  writeStoredSession,
} from "./session";
import { injectWidgetStyles } from "./styles";

export const WIDGET_ID = "indexaai-widget";
export const WIDGET_HOST_ID = "indexaai-widget-host";
const PANEL_ID = "indexaai-widget-panel";
const QUOTE_BODY_ID = "indexaai-widget-quote-body";
const MAX_PROMPTS = 4;
const MAX_CARDS = 3;
const MAX_MESSAGE_LENGTH = 1000;
// Tempo da animação de fechar (CSS): depois disso o painel sai do fluxo com hidden.
const CLOSE_MS = 160;
const IMAGE_BUBBLE_TEXT = "Quero encontrar um produto parecido com esta foto.";
const IMAGE_TURN_TEXT = "Quero encontrar um produto parecido com a foto que enviei.";
const QUOTE_NOTE = "Esta lista organiza os itens escolhidos. Os valores e a compra ficam na loja.";

const ERROR_TEXT: Record<AssistantFailure, string> = {
  disabled: "Este assistente não está disponível no momento.",
  quota: "Atingimos o limite de atendimento deste assistente por enquanto.",
  error: "Não consegui responder agora. Tente novamente em instantes.",
};

export interface AgentWidgetOptions {
  apiBase: string;
  publicKey: string;
  assistant: RemoteAssistantConfig | null;
  tracker: Tracker;
  // Chamado no envio, não na montagem: SPA troca de produto sem recarregar a página.
  getProduct: () => ProductContext | null;
  visitorId: string;
}

type Role = "bot" | "user" | "error";
type LivePriceState = AssistantProductPrice | { documentId: number; status: "loading" };

// Só createElement + textContent: nada de innerHTML com texto vindo do servidor.
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// Ícone escolhido no painel (Lucide), ou a imagem https que a loja enviou.
function buildIcon(look: ResolvedAppearance, className: string): Element {
  if (look.iconUrl) {
    const img = el("img");
    img.src = look.iconUrl;
    img.alt = "";
    return img;
  }
  return iconNode(look.icon, className);
}

// Foto do produto (só https); sem foto mostra o quadro vazio com a sacola, como o painel.
function mediaNode(
  imageUrl: string | null,
  name: string,
  className: string,
  emptyClass: string,
): HTMLElement {
  const src = toHttpsUrl(imageUrl);
  const node = el("div", src ? className : `${className} ${emptyClass}`);
  if (src) {
    const img = el("img");
    img.src = src;
    img.alt = "";
    img.loading = "lazy";
    img.addEventListener(
      "error",
      () => {
        node.classList.add(emptyClass);
        node.replaceChildren(iconNode("shopping-bag", ""));
      },
      { once: true },
    );
    node.setAttribute("role", "img");
    node.setAttribute("aria-label", name);
    node.append(img);
  } else {
    node.append(iconNode("shopping-bag", ""));
  }
  return node;
}

// Link da loja só com https: URL inválida some em vez de virar link.
function productLink(
  url: string | null,
  className: string,
  label: string,
): HTMLAnchorElement | null {
  const href = toHttpsUrl(url);
  if (!href) return null;

  const link = el("a", className);
  link.href = href;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.append(label, iconNode("external-link", ""));
  return link;
}

export function mountAgentWidget(options: AgentWidgetOptions): void {
  const { apiBase, publicKey, assistant, tracker, getProduct, visitorId } = options;
  if (!assistant || document.getElementById(WIDGET_HOST_ID)) return;

  const host = document.createElement("indexaai-assistant-widget");
  host.id = WIDGET_HOST_ID;
  host.style.cssText =
    "all: initial; position: fixed; inset: 0; z-index: 2147483647; pointer-events: none;";
  const shadowRoot = host.attachShadow({ mode: "open" });
  injectWidgetStyles(shadowRoot);

  const look = resolveAppearance(assistant.appearance);
  const name = (assistant.name || "").trim() || "Assistente";

  const root = el("div", "indexaai-widget");
  root.id = WIDGET_ID;
  root.style.pointerEvents = "none";
  root.dataset.anchor = look.anchor;
  const cssVars: Record<string, string> = {
    "--indexaai-primary": look.primary,
    "--indexaai-background": look.background,
    "--indexaai-text": look.text,
    "--indexaai-accent-fg": contrastColor(look.primary),
    "--indexaai-radius": look.radius,
    "--indexaai-font": look.font,
    "--indexaai-offset-x": `${look.offsetX}px`,
    "--indexaai-offset-y": `${look.offsetY}px`,
    "--indexaai-panel-x": panelOffsetCss(look.offsetX),
    "--indexaai-panel-y": `${panelBottomPx(look.offsetY)}px`,
  };
  for (const [prop, value] of Object.entries(cssVars)) root.style.setProperty(prop, value);

  // Lançador: 56 px, ícone centralizado, foco visível.
  const button = el("button", "indexaai-widget-button");
  button.type = "button";
  button.setAttribute("aria-label", `Abrir ${name}`);
  button.setAttribute("aria-expanded", "false");
  button.setAttribute("aria-controls", PANEL_ID);
  button.append(buildIcon(look, ""));

  const panel = el("div", "indexaai-widget-panel");
  panel.id = PANEL_ID;
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", name);
  panel.hidden = true;

  // Cabeçalho: avatar com o ícone, nome e fechar (X), como o painel.
  const avatar = el("div", "indexaai-widget-avatar");
  avatar.append(buildIcon(look, ""));
  const title = el("div", "indexaai-widget-title", name);
  const historyButton = el("button", "indexaai-widget-header-action");
  historyButton.type = "button";
  historyButton.setAttribute("aria-label", "Ver histórico de conversas");
  historyButton.title = "Histórico";
  historyButton.append(iconNode("history", ""));
  const newConversationButton = el("button", "indexaai-widget-header-action");
  newConversationButton.type = "button";
  newConversationButton.setAttribute("aria-label", "Nova conversa");
  newConversationButton.title = "Nova conversa";
  newConversationButton.append(iconNode("plus", ""));
  const closeButton = el("button", "indexaai-widget-close");
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Fechar assistente");
  closeButton.title = "Fechar";
  closeButton.append(iconNode("x", ""));
  const headerActions = el("div", "indexaai-widget-header-actions");
  headerActions.append(historyButton, newConversationButton, closeButton);
  const header = el("div", "indexaai-widget-header");
  header.append(avatar, title, headerActions);

  // A conversa mantém apenas mensagens. Orçamento e lista de produtos abrem em uma camada própria.
  const messages = el("div", "indexaai-widget-messages");
  messages.setAttribute("aria-live", "polite");
  const quoteSlot = el("div", "indexaai-widget-sheet-content");
  const sheetOverlay = el("div", "indexaai-widget-sheet");
  sheetOverlay.hidden = true;
  sheetOverlay.setAttribute("role", "dialog");
  sheetOverlay.setAttribute("aria-modal", "true");
  const sheetHeader = el("div", "indexaai-widget-sheet-header");
  const sheetHeading = el("div", "indexaai-widget-sheet-heading");
  const sheetTitle = el("h3", "indexaai-widget-sheet-title");
  const sheetSubtitle = el("p", "indexaai-widget-sheet-subtitle");
  sheetHeading.append(sheetTitle, sheetSubtitle);
  const sheetCloseButton = el("button", "indexaai-widget-sheet-close");
  sheetCloseButton.type = "button";
  sheetCloseButton.setAttribute("aria-label", "Fechar janela");
  sheetCloseButton.title = "Fechar";
  sheetCloseButton.append(iconNode("x", ""));
  sheetHeader.append(sheetHeading, sheetCloseButton);
  sheetOverlay.append(sheetHeader, quoteSlot);

  const chipsRow = el("div", "indexaai-widget-chips");

  const attachRow = el("div", "indexaai-widget-attach-row");
  attachRow.hidden = true;
  const attachThumb = el("img", "indexaai-widget-attach-thumb");
  attachThumb.alt = "";
  const attachName = el("span", "indexaai-widget-attach-name");
  const attachInfo = el("div", "indexaai-widget-attach-info");
  attachInfo.append(
    attachName,
    el("span", "indexaai-widget-attach-note", "A foto será enviada junto com sua mensagem"),
  );
  const attachRemove = el("button", "indexaai-widget-attach-remove");
  attachRemove.type = "button";
  attachRemove.setAttribute("aria-label", "Remover foto anexada");
  attachRemove.append(iconNode("x", ""));
  attachRow.append(attachThumb, attachInfo, attachRemove);

  const status = el("div", "indexaai-widget-status");
  status.setAttribute("aria-live", "polite");

  // Rodapé: anexar foto, campo e enviar (ícones Lucide, como o painel).
  const attachButton = el("button", "indexaai-widget-attach");
  attachButton.type = "button";
  attachButton.title = "Anexar foto";
  attachButton.setAttribute("aria-label", "Anexar foto");
  attachButton.append(iconNode("image-plus", ""));
  const fileInput = el("input");
  fileInput.type = "file";
  fileInput.accept = "image/*";
  fileInput.hidden = true;
  fileInput.setAttribute("aria-label", "Escolher foto para enviar na conversa");
  const input = el("input", "indexaai-widget-input");
  input.type = "text";
  input.maxLength = MAX_MESSAGE_LENGTH;
  input.placeholder = "Escreva sua mensagem…";
  input.setAttribute("aria-label", "Sua mensagem");
  const quoteButton = el("button", "indexaai-widget-quote-button");
  quoteButton.type = "button";
  quoteButton.hidden = true;
  quoteButton.title = "Ver orçamento";
  quoteButton.setAttribute("aria-label", "Ver orçamento");
  quoteButton.setAttribute("aria-expanded", "false");
  quoteButton.append(iconNode("clipboard-list", ""), el("span", "indexaai-widget-quote-badge"));
  const sendButton = el("button", "indexaai-widget-send");
  sendButton.type = "submit";
  sendButton.setAttribute("aria-label", "Enviar mensagem");
  sendButton.append(iconNode("send", ""));
  const form = el("form", "indexaai-widget-form");
  form.append(attachButton, fileInput, input, quoteButton, sendButton);

  const feedbackButton = el("button", "indexaai-widget-feedback-link", "Enviar feedback");
  feedbackButton.type = "button";
  const poweredLink = el("a", "indexaai-widget-powered-link", "Powered by IndexaAI");
  poweredLink.href = "https://indexaai.com";
  poweredLink.target = "_blank";
  poweredLink.rel = "noopener noreferrer";
  const footer = el("div", "indexaai-widget-footer");
  footer.append(feedbackButton, poweredLink);

  panel.append(header, messages, attachRow, status, form, footer, sheetOverlay);
  root.append(button, panel);

  // Estado: sessão fica na memória e no sessionStorage (chave com a publicKey). Orçamento vem de cada resposta.
  let pending = false;
  let isOpen = false;
  let closeTimer: ReturnType<typeof setTimeout> | null = null;
  let session: AssistantSession | null = readStoredSession(publicKey);
  let quoteItems: AssistantQuoteItem[] = [];
  const livePrices = new Map<number, LivePriceState>();
  const livePriceFetchedAt = new Map<number, number>();
  let priceGeneration = 0;
  let requestLivePrices = (_documentIds: number[]): void => {};
  let quoteOpen = true;
  let sheetView: "quote" | "products" | null = null;
  let availableProducts: AssistantProductCard[] = [];
  let hasMoreProductResults = false;
  let sheetTrigger: HTMLElement | null = null;
  let view: "chat" | "history" | "feedback" = "chat";
  let conversationRendered = false;
  let restoreRequest = 0;
  let historyEntries = readStoredAssistantHistory(publicKey, visitorId);
  let clientSessionId: string | undefined =
    (isActive(session) && session.clientSessionId) ||
    historyEntries.find((entry) => entry.sessionId === session?.sessionId)?.clientSessionId ||
    readPendingClientSessionId(publicKey);
  let pendingImage: File | null = null;
  let pendingImageUrl: string | null = null;
  const chipButtons: HTMLButtonElement[] = [];
  const prompts = (Array.isArray(assistant.suggestedPrompts) ? assistant.suggestedPrompts : [])
    .filter((prompt) => typeof prompt === "string" && prompt.trim() !== "")
    .slice(0, MAX_PROMPTS);

  for (const prompt of prompts) {
    const chip = el("button", "indexaai-widget-chip", prompt);
    chip.type = "button";
    chip.addEventListener("click", () => void sendTurn(prompt));
    chipButtons.push(chip);
    chipsRow.append(chip);
  }

  const addNode = (node: HTMLElement): void => {
    messages.append(node);
    messages.scrollTop = messages.scrollHeight;
  };

  let detectedCurrency: string | null | undefined;
  const pageCurrency = (): string | null => {
    if (detectedCurrency !== undefined) return detectedCurrency;
    const metaCurrency =
      document.querySelector<HTMLMetaElement>('meta[itemprop="priceCurrency"]')?.content ??
      document.querySelector<HTMLMetaElement>('meta[property="product:price:currency"]')?.content ??
      document.querySelector<HTMLMetaElement>('meta[property="og:price:currency"]')?.content;
    if (metaCurrency && /^[A-Za-z]{3}$/.test(metaCurrency)) {
      detectedCurrency = metaCurrency.toUpperCase();
      return detectedCurrency;
    }

    const readJsonLdCurrency = (value: unknown): string | null => {
      if (Array.isArray(value)) {
        for (const item of value) {
          const currency = readJsonLdCurrency(item);
          if (currency) return currency;
        }
      } else if (typeof value === "object" && value !== null) {
        const object = value as Record<string, unknown>;
        if (
          typeof object.priceCurrency === "string" &&
          /^[A-Za-z]{3}$/.test(object.priceCurrency)
        ) {
          return object.priceCurrency.toUpperCase();
        }
        for (const item of Object.values(object)) {
          const currency = readJsonLdCurrency(item);
          if (currency) return currency;
        }
      }
      return null;
    };

    for (const script of Array.from(
      document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]'),
    )) {
      try {
        const currency = readJsonLdCurrency(JSON.parse(script.textContent ?? ""));
        if (currency) {
          detectedCurrency = currency;
          return detectedCurrency;
        }
      } catch {
        // Ignora JSON-LD inválido da loja e tenta os próximos metadados.
      }
    }

    const locale = document.documentElement.lang || navigator.language || "pt-BR";
    detectedCurrency = locale.toLowerCase().startsWith("pt") ? "BRL" : null;
    return detectedCurrency;
  };

  const formatPriceAmount = (value: string): string => {
    const amount = Number(value.replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) return "Preço sob consulta";
    const locale = document.documentElement.lang || navigator.language || "pt-BR";
    const currency = pageCurrency();
    try {
      return new Intl.NumberFormat(
        locale,
        currency
          ? { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }
          : { minimumFractionDigits: 2, maximumFractionDigits: 2 },
      ).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  };

  const renderLivePriceNode = (node: HTMLElement, documentId: number): void => {
    const price = livePrices.get(documentId) ?? { documentId, status: "loading" as const };
    node.replaceChildren();
    node.setAttribute("aria-live", "polite");
    node.classList.remove("indexaai-widget-live-price--loading");

    if (price.status === "loading") {
      node.classList.add("indexaai-widget-live-price--loading");
      node.append(el("span", "indexaai-widget-price-skeleton"));
      node.setAttribute("aria-label", "Consultando preço na loja");
      return;
    }

    node.removeAttribute("aria-label");
    if (price.status === "unavailable") {
      node.textContent = "Preço sob consulta";
      return;
    }

    const quantity = Math.max(1, Number(node.dataset.quantity ?? "1"));
    if (price.status === "available") {
      const currentPriceAmount = Number(price.price.replace(",", "."));
      if (!Number.isFinite(currentPriceAmount) || currentPriceAmount <= 0) {
        node.textContent = "Preço sob consulta";
        return;
      }
      const unitPrice = formatPriceAmount(price.price);
      const originalPriceValue = price.originalPrice;
      const originalPriceAmount = originalPriceValue
        ? Number(originalPriceValue.replace(",", "."))
        : null;
      const hasPromotion =
        originalPriceAmount !== null &&
        Number.isFinite(originalPriceAmount) &&
        originalPriceAmount > currentPriceAmount;
      const originalPrice =
        hasPromotion && originalPriceValue ? formatPriceAmount(originalPriceValue) : "";
      const quoteTotal = formatPriceAmount((currentPriceAmount * quantity).toFixed(2));
      node.textContent =
        node.dataset.priceMode === "quote"
          ? hasPromotion
            ? `De ${originalPrice} por ${unitPrice} · ${quantity} un.: ${quoteTotal}`
            : `${unitPrice} × ${quantity} = ${quoteTotal}`
          : hasPromotion
            ? `De ${originalPrice} por ${unitPrice}`
            : `Preço atual: ${unitPrice}`;
      return;
    }

    const range = `${formatPriceAmount(price.minPrice)} a ${formatPriceAmount(price.maxPrice)}`;
    node.textContent =
      node.dataset.priceMode === "quote" ? `Faixa de preço: ${range}` : `Faixa: ${range}`;
  };

  const createLivePriceNode = (
    documentId: number,
    mode: "card" | "quote",
    quantity = 1,
  ): HTMLElement => {
    const node = el("span", "indexaai-widget-live-price");
    node.dataset.livePriceDocumentId = String(documentId);
    node.dataset.priceMode = mode;
    node.dataset.quantity = String(quantity);
    renderLivePriceNode(node, documentId);
    return node;
  };

  const closeSheet = (restoreFocus = true): void => {
    sheetView = null;
    sheetOverlay.hidden = true;
    quoteButton.setAttribute("aria-expanded", "false");
    const trigger = sheetTrigger;
    sheetTrigger = null;
    if (restoreFocus && trigger?.isConnected) trigger.focus();
  };

  const bubble = (role: Role, text: string, createdAt?: string): HTMLElement => {
    const node = el("div", `indexaai-widget-message indexaai-widget-message--${role}`);
    node.append(el("span", "indexaai-widget-message-copy", text));
    const date = createdAt ? new Date(createdAt) : null;
    if (date && Number.isFinite(date.getTime())) {
      const time = new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
      const timeNode = el("time", "indexaai-widget-message-time", time);
      timeNode.dateTime = date.toISOString();
      timeNode.title = new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
      node.append(timeNode);
    }
    return node;
  };

  const setPending = (value: boolean): void => {
    pending = value;
    input.disabled = value;
    sendButton.disabled = value;
    attachButton.disabled = value;
    for (const chip of chipButtons) chip.disabled = value;
  };

  const showAttachment = (file: File, url: string): void => {
    attachThumb.src = url;
    attachName.textContent = file.name;
    attachRow.hidden = false;
  };

  // Solta a foto do campo sem revogar a URL: ela pode já estar numa bolha enviada.
  const releaseAttachment = (): void => {
    pendingImage = null;
    pendingImageUrl = null;
    attachRow.hidden = true;
    fileInput.value = "";
  };

  const clearAttachment = (): void => {
    if (pendingImageUrl) URL.revokeObjectURL(pendingImageUrl);
    releaseAttachment();
  };

  const attachImage = (file: File): void => {
    const problem = validateImageFile(file);
    if (problem) {
      status.textContent = problem;
      fileInput.value = "";
      return;
    }
    clearAttachment();
    status.textContent = "";
    pendingImage = file;
    pendingImageUrl = URL.createObjectURL(file);
    showAttachment(file, pendingImageUrl);
  };

  const activeSessionId = (): number | undefined =>
    isActive(session) ? session.sessionId : undefined;

  const ensureClientSessionId = (): string => {
    if (!clientSessionId) {
      const nextClientSessionId =
        typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
              const random = (crypto.getRandomValues(new Uint8Array(1))[0] ?? 0) & 15;
              return (char === "x" ? random : (random & 3) | 8).toString(16);
            });
      clientSessionId = nextClientSessionId;
      writePendingClientSessionId(publicKey, nextClientSessionId);
    }
    return clientSessionId;
  };

  const rememberSession = (
    reply: AssistantReply,
    lastActivityAt = new Date().toISOString(),
  ): void => {
    const next: AssistantSession = {
      sessionId: reply.sessionId,
      expiresAt: reply.expiresAt,
      ...(clientSessionId ? { clientSessionId } : {}),
    };
    session = next;
    if (Number.isFinite(Date.parse(reply.expiresAt))) writeStoredSession(publicKey, next);
    else removeStoredSession(publicKey);
    historyEntries = rememberAssistantConversation(publicKey, visitorId, {
      sessionId: reply.sessionId,
      ...(clientSessionId ? { clientSessionId } : {}),
      expiresAt: reply.expiresAt,
      lastActivityAt,
    });
    if (clientSessionId) removePendingClientSessionId(publicKey);
    newConversationButton.hidden = false;
  };

  const setView = (nextView: "chat" | "history" | "feedback"): void => {
    if (nextView !== "chat") closeSheet(false);
    view = nextView;
    title.textContent =
      nextView === "history"
        ? "Conversas recentes"
        : nextView === "feedback"
          ? "Enviar feedback"
          : name;
    historyButton.replaceChildren(iconNode(nextView === "chat" ? "history" : "arrow-left", ""));
    historyButton.setAttribute(
      "aria-label",
      nextView === "chat" ? "Ver histórico de conversas" : "Voltar para a conversa",
    );
    historyButton.title = nextView === "chat" ? "Histórico" : "Voltar";
    newConversationButton.hidden =
      nextView === "feedback" || (nextView === "chat" && !isActive(session));
    form.hidden = nextView !== "chat";
    attachRow.hidden = true;
    footer.hidden = nextView !== "chat";
    status.textContent = "";
  };

  const rememberCurrentSession = (): void => {
    if (!isActive(session)) return;
    const existing = historyEntries.find((entry) => entry.sessionId === session?.sessionId);
    const startedAt = new Date(Date.parse(session.expiresAt) - 24 * 60 * 60 * 1000).toISOString();
    historyEntries = rememberAssistantConversation(publicKey, visitorId, {
      sessionId: session.sessionId,
      ...(session.clientSessionId ? { clientSessionId: session.clientSessionId } : {}),
      expiresAt: session.expiresAt,
      lastActivityAt: existing?.lastActivityAt ?? startedAt,
    });
  };

  const formatHistoryTime = (value: string): string => {
    const date = new Date(value);
    return Number.isFinite(date.getTime())
      ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date)
      : "Conversa recente";
  };

  const renderWelcome = (): void => {
    setView("chat");
    conversationRendered = true;
    priceGeneration += 1;
    livePrices.clear();
    livePriceFetchedAt.clear();
    chipsRow.hidden = false;
    quoteItems = [];
    availableProducts = [];
    hasMoreProductResults = false;
    closeSheet(false);
    messages.replaceChildren();
    if (assistant.welcomeMessage)
      addNode(bubble("bot", assistant.welcomeMessage, new Date().toISOString()));
    if (chipButtons.length > 0) addNode(chipsRow);
    renderQuote();
  };

  const renderHistory = (): void => {
    rememberCurrentSession();
    historyEntries = readStoredAssistantHistory(publicKey, visitorId);
    setView("history");
    chipsRow.hidden = true;
    closeSheet(false);
    messages.replaceChildren();

    const list = el("section", "indexaai-widget-history");
    list.setAttribute("aria-label", "Histórico de conversas");
    list.append(
      el("h3", "indexaai-widget-history-heading", "Conversas recentes"),
      el("p", "indexaai-widget-history-note", "Disponíveis por até 24 horas neste navegador."),
    );

    if (historyEntries.length === 0) {
      const empty = el("div", "indexaai-widget-history-empty");
      empty.append(iconNode("history", ""), el("strong", "", "Nenhuma conversa recente"));
      empty.append(el("span", "", "Suas conversas aparecerão aqui depois da primeira mensagem."));
      list.append(empty);
    } else {
      for (const entry of historyEntries) {
        const item = el("button", "indexaai-widget-history-item");
        item.type = "button";
        item.setAttribute(
          "aria-label",
          `Abrir conversa de ${formatHistoryTime(entry.lastActivityAt)}`,
        );
        const icon = el("span", "indexaai-widget-history-item-icon");
        icon.append(iconNode("message-square", ""));
        const copy = el("span", "indexaai-widget-history-item-copy");
        copy.append(
          el("span", "indexaai-widget-history-item-title", "Conversa da loja"),
          el("span", "indexaai-widget-history-item-time", formatHistoryTime(entry.lastActivityAt)),
        );
        item.append(icon, copy);
        item.addEventListener("click", () => void openStoredConversation(entry, true));
        list.append(item);
      }
    }
    addNode(list);
  };

  const openStoredConversation = async (
    entry: StoredAssistantConversation,
    fromHistory: boolean,
  ): Promise<void> => {
    const requestId = ++restoreRequest;
    priceGeneration += 1;
    livePrices.clear();
    livePriceFetchedAt.clear();
    clientSessionId = entry.clientSessionId;
    setView("chat");
    chipsRow.hidden = true;
    closeSheet(false);
    messages.replaceChildren();
    addNode(el("div", "indexaai-widget-history-note", "Carregando conversa…"));
    setPending(true);

    const result = await loadAssistantSession(apiBase, publicKey, {
      sessionId: entry.sessionId,
      visitorId,
    });
    if (requestId !== restoreRequest) return;

    if (!result.ok) {
      setPending(false);
      if (result.reason === "expired") {
        historyEntries = forgetAssistantConversation(publicKey, visitorId, entry.sessionId);
        if (session?.sessionId === entry.sessionId) {
          session = null;
          removeStoredSession(publicKey);
        }
        if (fromHistory) renderHistory();
        else renderWelcome();
        status.textContent = fromHistory
          ? "Essa conversa expirou e foi removida do histórico."
          : "Essa conversa expirou. Comece uma nova conversa.";
      } else {
        messages.replaceChildren();
        closeSheet(false);
        addNode(bubble("error", "Não foi possível carregar esta conversa. Tente novamente."));
        conversationRendered = false;
      }
      return;
    }

    session = {
      sessionId: result.session.sessionId,
      expiresAt: result.session.expiresAt,
      ...(clientSessionId ? { clientSessionId } : {}),
    };
    writeStoredSession(publicKey, session);
    quoteItems = result.session.quoteItems;
    availableProducts = [];
    hasMoreProductResults = false;
    messages.replaceChildren();
    const restoredProductIds: number[] = [];
    for (const message of result.session.messages) {
      const messageNode = bubble(
        message.role === "assistant" ? "bot" : "user",
        message.content,
        message.createdAt,
      );
      if (message.imageCaption) {
        messageNode.append(
          el("span", "indexaai-widget-caption", `A foto mostra: ${message.imageCaption}`),
        );
      }
      addNode(messageNode);
      if (message.role === "assistant") {
        availableProducts = message.products ?? [];
        hasMoreProductResults = message.hasMoreProducts === true;
      }
      if (message.products?.length) {
        restoredProductIds.push(...message.products.map((product) => product.documentId));
        addNode(buildCards(message.products, message.hasMoreProducts === true));
      }
      const followUp = buildFollowUpChips(message.categories ?? []);
      if (followUp) addNode(followUp);
    }
    const lastCreatedAt = result.session.messages[result.session.messages.length - 1]?.createdAt;
    rememberSession(
      { ...result.session, answer: "", products: [], quoteItems: result.session.quoteItems },
      lastCreatedAt && Number.isFinite(Date.parse(lastCreatedAt))
        ? lastCreatedAt
        : entry.lastActivityAt,
    );
    renderQuote();
    requestLivePrices([...restoredProductIds, ...quoteItems.map((item) => item.documentId)]);
    conversationRendered = true;
    setPending(false);
    messages.scrollTop = messages.scrollHeight;
  };

  const restoreCurrentSession = async (): Promise<void> => {
    if (!isActive(session)) {
      if (session) removeStoredSession(publicKey);
      session = null;
      renderWelcome();
      return;
    }
    rememberCurrentSession();
    if (isActive(session)) clientSessionId = session.clientSessionId;
    const entry = historyEntries.find((item) => item.sessionId === session?.sessionId);
    const startedAt = new Date(Date.parse(session.expiresAt) - 24 * 60 * 60 * 1000).toISOString();
    await openStoredConversation(
      entry ?? {
        sessionId: session.sessionId,
        expiresAt: session.expiresAt,
        lastActivityAt: startedAt,
      },
      false,
    );
  };

  const startNewConversation = (): void => {
    if (pending) return;
    rememberCurrentSession();
    session = null;
    removeStoredSession(publicKey);
    clientSessionId = undefined;
    removePendingClientSessionId(publicKey);
    ensureClientSessionId();
    restoreRequest += 1;
    renderWelcome();
    newConversationButton.hidden = true;
    input.focus();
  };

  const renderFeedback = (): void => {
    setView("feedback");
    chipsRow.hidden = true;
    closeSheet(false);
    messages.replaceChildren();

    const feedbackForm = el("form", "indexaai-widget-feedback");
    const kind = { value: "suggestion" as "bug" | "suggestion" };
    feedbackForm.append(
      el("h3", "indexaai-widget-feedback-title", "Ajude a melhorar o assistente"),
      el(
        "p",
        "indexaai-widget-feedback-note",
        "Conte o que não funcionou ou o que seria útil. Não inclua dados pessoais ou de pagamento.",
      ),
    );

    const options = el("div", "indexaai-widget-feedback-options");
    options.setAttribute("role", "group");
    options.setAttribute("aria-label", "Tipo de feedback");
    const optionButtons: HTMLButtonElement[] = [];
    for (const option of [
      { value: "bug" as const, label: "Relatar problema" },
      { value: "suggestion" as const, label: "Dar sugestão" },
    ]) {
      const optionButton = el("button", "indexaai-widget-feedback-option", option.label);
      optionButton.type = "button";
      optionButton.setAttribute("aria-pressed", String(kind.value === option.value));
      optionButton.addEventListener("click", () => {
        kind.value = option.value;
        for (const button of optionButtons) {
          button.setAttribute("aria-pressed", String(button.dataset.value === kind.value));
        }
      });
      optionButton.dataset.value = option.value;
      optionButtons.push(optionButton);
      options.append(optionButton);
    }

    const textarea = el("textarea", "indexaai-widget-feedback-textarea");
    textarea.maxLength = 1200;
    textarea.required = true;
    textarea.placeholder = "Escreva aqui o seu relato…";
    textarea.setAttribute("aria-label", "Descreva o problema ou a sugestão");
    const formStatus = el("p", "indexaai-widget-feedback-note");
    formStatus.setAttribute("role", "status");
    const submit = el("button", "indexaai-widget-feedback-submit", "Enviar feedback");
    submit.type = "submit";
    feedbackForm.append(options, textarea, formStatus, submit);
    feedbackForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      const message = textarea.value.trim();
      if (message.length < 10) {
        formStatus.textContent = "Escreva pelo menos 10 caracteres para enviar.";
        textarea.focus();
        return;
      }

      submit.disabled = true;
      formStatus.textContent = "Enviando…";
      const result = await sendAssistantFeedback(apiBase, publicKey, {
        visitorId,
        type: kind.value,
        message,
        ...(isActive(session) ? { sessionId: session.sessionId } : {}),
      });
      if (!result.ok) {
        formStatus.textContent =
          result.reason === "rate-limited"
            ? "Você enviou feedback há pouco. Tente novamente em alguns minutos."
            : "Não foi possível enviar agora. Tente novamente em instantes.";
        submit.disabled = false;
        return;
      }

      const success = el("div", "indexaai-widget-feedback-success");
      success.append(
        iconNode("sparkles", ""),
        el("strong", "", "Feedback recebido"),
        el("span", "", "Obrigado por ajudar a melhorar o assistente da loja."),
      );
      const backButton = el("button", "indexaai-widget-feedback-submit", "Voltar à conversa");
      backButton.type = "button";
      backButton.addEventListener("click", () => {
        if (isActive(session)) void restoreCurrentSession();
        else renderWelcome();
      });
      success.append(backButton);
      messages.replaceChildren(success);
      closeSheet(false);
    });
    addNode(feedbackForm);
  };

  const addLabel = (documentId: number): string =>
    quoteItems.some((item) => item.documentId === documentId)
      ? "Adicionar mais uma"
      : "Adicionar ao orçamento";

  // Botões "Adicionar" dos cards acompanham o orçamento atual, como no painel.
  const refreshAddLabels = (): void => {
    for (const add of Array.from(
      root.querySelectorAll<HTMLButtonElement>("button[data-document-id]"),
    )) {
      const label = add.querySelector(".indexaai-widget-add-label");
      if (label) label.textContent = addLabel(Number(add.dataset.documentId));
    }
  };

  const quoteRow = (item: AssistantQuoteItem): HTMLElement => {
    const row = el("li", "indexaai-widget-quote-item");
    const info = el("div", "indexaai-widget-quote-info");
    info.append(
      el("span", "indexaai-widget-quote-name", item.name),
      el("span", "indexaai-widget-quote-qty", `Qtd. ${item.quantity}`),
    );
    if (assistant.showLivePrices === true)
      info.append(createLivePriceNode(item.documentId, "quote", item.quantity));
    const link = productLink(item.url, "indexaai-widget-quote-link", "Ver na loja");
    if (link) info.append(link);

    const remove = el("button", "indexaai-widget-quote-remove");
    remove.type = "button";
    remove.setAttribute("aria-label", `Remover ${item.name} do orçamento`);
    remove.append(iconNode("minus", ""));
    remove.addEventListener(
      "click",
      () =>
        void sendTurn(`Remova ${item.name} do meu orçamento.`, {
          action: { type: "remove", productDocumentId: item.documentId },
        }),
    );

    row.append(
      mediaNode(
        item.imageUrl,
        item.name,
        "indexaai-widget-quote-thumb",
        "indexaai-widget-quote-thumb--empty",
      ),
      info,
      remove,
    );
    return row;
  };

  const buildQuote = (): HTMLElement => {
    // "1 produto" para um item de quantidade 1; senão "N produtos · M unidades" (M = soma das quantidades).
    const products = quoteItems.length;
    const units = quoteItems.reduce((total, item) => total + item.quantity, 0);
    const productText = `${products} ${products === 1 ? "produto" : "produtos"}`;
    const summary = units > 1 ? `${productText} · ${units} unidades` : productText;

    const section = el("section", "indexaai-widget-quote");
    section.setAttribute("aria-label", "Orçamento da conversa");

    const head = el("button", "indexaai-widget-quote-head");
    head.type = "button";
    head.setAttribute("aria-expanded", String(quoteOpen));
    head.setAttribute("aria-controls", QUOTE_BODY_ID);
    const icon = el("span", "indexaai-widget-quote-icon");
    icon.append(iconNode("clipboard-list", ""));
    const titles = el("div", "indexaai-widget-quote-titles");
    titles.append(
      el("div", "indexaai-widget-quote-title", "Seu orçamento"),
      el(
        "div",
        "indexaai-widget-quote-sub",
        assistant.showLivePrices === true
          ? "Preços atuais consultados na loja"
          : "Os valores são consultados na loja",
      ),
    );
    const chevron = el("span", "indexaai-widget-quote-chevron");
    chevron.dataset.open = String(quoteOpen);
    chevron.append(iconNode("chevron-down", ""));
    head.append(icon, titles, el("span", "indexaai-widget-quote-count", summary), chevron);
    head.addEventListener("click", () => {
      quoteOpen = !quoteOpen;
      renderQuote();
    });

    const body = el("div");
    body.id = QUOTE_BODY_ID;
    body.hidden = !quoteOpen;
    const list = el("ul", "indexaai-widget-quote-list");
    for (const item of quoteItems) list.append(quoteRow(item));
    body.append(list);
    if (assistant.showLivePrices === true) {
      const exactPrices = quoteItems.map((item) => ({
        item,
        price: livePrices.get(item.documentId),
      }));
      const allExact = exactPrices.every((entry) => {
        if (entry.price?.status !== "available") return false;
        const amount = Number(entry.price.price.replace(",", "."));
        return Number.isFinite(amount) && amount > 0;
      });
      const total = el("p", "indexaai-widget-quote-total");
      if (allExact) {
        const amount = exactPrices.reduce((sum, entry) => {
          const price = entry.price;
          return (
            sum + (price?.status === "available" ? Number(price.price) * entry.item.quantity : 0)
          );
        }, 0);
        total.textContent = `Total dos produtos: ${formatPriceAmount(amount.toFixed(2))}`;
      } else if (exactPrices.some((entry) => !entry.price || entry.price.status === "loading")) {
        total.textContent = "Consultando preços para calcular o total…";
      } else {
        total.textContent = "Total indisponível até confirmar todos os itens.";
      }
      body.append(
        total,
        el(
          "p",
          "indexaai-widget-quote-foot",
          "Valores atuais da loja; o checkout confirma o preço final.",
        ),
      );
    } else {
      body.append(el("p", "indexaai-widget-quote-foot", QUOTE_NOTE));
    }

    section.append(head, body);
    return section;
  };

  const renderQuote = (): void => {
    quoteSlot.replaceChildren();
    const itemCount = quoteItems.reduce((total, item) => total + item.quantity, 0);
    quoteButton.hidden = quoteItems.length === 0;
    quoteButton.setAttribute(
      "aria-label",
      `Ver orçamento com ${itemCount} ${itemCount === 1 ? "item" : "itens"}`,
    );
    quoteButton.title = `Ver orçamento (${itemCount})`;
    const badge = quoteButton.querySelector(".indexaai-widget-quote-badge");
    if (badge) badge.textContent = String(itemCount);
    if (sheetView === "products") renderProductSheet();
    else if (sheetView === "quote" && quoteItems.length > 0) quoteSlot.append(buildQuote());
    else if (sheetView === "quote") closeSheet(false);
    refreshAddLabels();
  };

  requestLivePrices = (documentIds: number[]): void => {
    if (assistant.showLivePrices !== true) return;
    const ids = [...new Set(documentIds)].filter(
      (documentId) => Number.isInteger(documentId) && documentId > 0,
    );
    const now = Date.now();
    const pendingIds = ids.filter((documentId) => {
      const price = livePrices.get(documentId);
      const fetchedAt = livePriceFetchedAt.get(documentId);
      return !price || (price.status !== "loading" && (!fetchedAt || now - fetchedAt >= 60_000));
    });
    if (pendingIds.length === 0) return;

    for (const documentId of pendingIds) {
      livePrices.set(documentId, { documentId, status: "loading" });
      livePriceFetchedAt.delete(documentId);
    }
    const generation = priceGeneration;
    const refreshViews = (): void => {
      renderQuote();
      for (const node of Array.from(
        root.querySelectorAll<HTMLElement>("[data-live-price-document-id]"),
      )) {
        const documentId = Number(node.dataset.livePriceDocumentId);
        if (Number.isInteger(documentId)) renderLivePriceNode(node, documentId);
      }
    };
    refreshViews();

    const batches: number[][] = [];
    for (let index = 0; index < pendingIds.length; index += 12)
      batches.push(pendingIds.slice(index, index + 12));
    void Promise.all(batches.map((batch) => fetchAssistantPrices(apiBase, publicKey, batch))).then(
      (responses) => {
        if (generation !== priceGeneration) return;
        for (const price of responses.flat()) {
          livePrices.set(price.documentId, price);
          livePriceFetchedAt.set(price.documentId, Date.now());
        }
        refreshViews();
      },
    );
  };

  const setSheetHeading = (heading: string, description: string): void => {
    sheetTitle.textContent = heading;
    sheetSubtitle.textContent = description;
    sheetOverlay.setAttribute("aria-label", heading);
  };

  const openQuoteSheet = (): void => {
    if (quoteItems.length === 0) return;
    sheetView = "quote";
    sheetTrigger = quoteButton;
    quoteOpen = true;
    setSheetHeading("Seu orçamento", "Os valores e a compra são consultados na loja.");
    renderQuote();
    sheetOverlay.hidden = false;
    quoteButton.setAttribute("aria-expanded", "true");
    sheetCloseButton.focus();
  };

  const mergeProducts = (
    current: AssistantProductCard[],
    incoming: AssistantProductCard[],
  ): AssistantProductCard[] => {
    const merged = new Map(current.map((product) => [product.documentId, product]));
    for (const product of incoming) merged.set(product.documentId, product);
    return [...merged.values()];
  };

  const renderProductSheet = (): void => {
    if (sheetView !== "products") return;
    quoteSlot.replaceChildren();
    if (availableProducts.length > 0) {
      const list = el("div", "indexaai-widget-sheet-products");
      availableProducts.forEach((product, index) => list.append(buildCard(product, index)));
      quoteSlot.append(list);
    } else {
      quoteSlot.append(
        el(
          "div",
          "indexaai-widget-sheet-empty",
          "Ainda não encontrei outras opções para esta busca.",
        ),
      );
    }

    if (hasMoreProductResults) {
      const more = el(
        "button",
        "indexaai-widget-sheet-more",
        pending ? "Buscando opções…" : "Carregar mais opções",
      );
      more.type = "button";
      more.disabled = pending;
      more.append(iconNode("chevron-down", ""));
      more.addEventListener(
        "click",
        () =>
          void sendTurn("Quero ver mais opções do que estávamos procurando.", { isLoadMore: true }),
      );
      quoteSlot.append(more);
    } else if (availableProducts.length > 0) {
      quoteSlot.append(
        el(
          "p",
          "indexaai-widget-sheet-note",
          "Estas são as opções encontradas para o que você procura.",
        ),
      );
    }
    refreshAddLabels();
  };

  const openProductsSheet = (
    products: AssistantProductCard[],
    hasMore: boolean,
    trigger: HTMLButtonElement,
  ): void => {
    sheetView = "products";
    sheetTrigger = trigger;
    availableProducts = products;
    hasMoreProductResults = hasMore;
    setSheetHeading(
      "Opções de produtos",
      "Veja os itens sugeridos em uma lista fácil de percorrer.",
    );
    renderProductSheet();
    sheetOverlay.hidden = false;
    sheetCloseButton.focus();
  };

  const buildCard = (product: AssistantProductCard, index: number): HTMLElement => {
    const card = el("article", "indexaai-widget-card");
    card.style.setProperty("--indexaai-i", String(index));

    const body = el("div", "indexaai-widget-card-body");
    const title = el("p", "indexaai-widget-card-name", product.name);
    title.title = product.name;
    body.append(title);
    const category = product.categories?.[0];
    if (category) {
      const parts = category.split(/\s*>\s*/).filter(Boolean);
      const label = parts[parts.length - 1] ?? category;
      const categoryNode = el("span", "indexaai-widget-category", label);
      categoryNode.title = category;
      categoryNode.setAttribute("aria-label", `Categoria: ${category}`);
      body.append(categoryNode);
    }
    if (assistant.showLivePrices === true)
      body.append(createLivePriceNode(product.documentId, "card"));

    const link = productLink(
      product.url,
      "indexaai-widget-btn indexaai-widget-card-link",
      "Ver na loja",
    );
    if (link) body.append(link);

    const ask = el("button", "indexaai-widget-btn indexaai-widget-card-ask", "Tirar dúvida");
    ask.type = "button";
    ask.addEventListener("click", () => {
      closeSheet(false);
      void sendTurn(`Quero tirar uma dúvida sobre ${product.name}.`);
    });

    const add = el("button", "indexaai-widget-btn indexaai-widget-card-add");
    add.type = "button";
    add.dataset.documentId = String(product.documentId);
    add.append(
      iconNode("plus", ""),
      el("span", "indexaai-widget-add-label", addLabel(product.documentId)),
    );
    add.addEventListener("click", () => {
      closeSheet(false);
      void sendTurn(`Inclua ${product.name} no meu orçamento.`, {
        action: { type: "add", productDocumentId: product.documentId },
      });
    });
    const actions = el("div", "indexaai-widget-card-actions");
    actions.append(ask, add);
    body.append(actions);

    card.append(
      mediaNode(
        product.imageUrl,
        product.name,
        "indexaai-widget-card-media",
        "indexaai-widget-card-media--empty",
      ),
      body,
    );
    return card;
  };

  const buildCards = (products: AssistantProductCard[], hasMore = false): HTMLElement => {
    const wrap = el("div");
    wrap.append(el("p", "indexaai-widget-cards-title", "Produtos para você"));
    const row = el("div", "indexaai-widget-cards");
    row.setAttribute("aria-label", "Produtos sugeridos");
    products.slice(0, MAX_CARDS).forEach((product, index) => row.append(buildCard(product, index)));
    wrap.append(row);
    if (products.length > 0) {
      const more = el(
        "button",
        "indexaai-widget-products-more",
        hasMore ? "Ver mais opções" : "Abrir lista de produtos",
      );
      more.type = "button";
      more.setAttribute(
        "aria-label",
        hasMore ? "Ver mais opções em uma lista" : "Abrir lista de produtos",
      );
      more.append(iconNode("chevron-down", ""));
      more.addEventListener("click", () => openProductsSheet(products, hasMore, more));
      wrap.append(more);
    }
    return wrap;
  };

  const buildFollowUpChips = (categories: string[]): HTMLElement | null => {
    const actions: Array<{ label: string; prompt: string }> = [];
    for (const category of categories.slice(0, 4)) {
      const label =
        category
          .split(/\s*>\s*/)
          .filter(Boolean)
          .slice(-1)[0] ?? category;
      actions.push({ label, prompt: `Quero ver produtos da categoria ${category}.` });
    }
    if (actions.length === 0) return null;

    const row = el("div", "indexaai-widget-chips");
    row.setAttribute("aria-label", "Continuar explorando o catálogo");
    for (const action of actions) {
      const chip = el("button", "indexaai-widget-chip", action.label);
      chip.type = "button";
      chip.addEventListener("click", () => void sendTurn(action.prompt));
      row.append(chip);
    }
    return row;
  };

  // Um turno: foto (se houver) -> busca -> mensagem com sessionId/ação. Erro restaura o que o usuário digitou.
  const sendTurn = async (
    text: string,
    options: { action?: AssistantAction; fromForm?: boolean; isLoadMore?: boolean } = {},
  ): Promise<void> => {
    const message = text.trim();
    const image = options.action ? null : pendingImage;
    if ((!message && !image) || pending || view !== "chat") return;

    if (!options.action && !options.isLoadMore) {
      availableProducts = [];
      hasMoreProductResults = false;
    }

    setPending(true);
    renderProductSheet();
    chipsRow.hidden = true;
    conversationRendered = true;
    status.textContent = "";
    if (options.fromForm) input.value = "";
    const imageUrl = image ? pendingImageUrl : null;
    if (image) releaseAttachment();

    const userBubble = bubble("user", message || IMAGE_BUBBLE_TEXT, new Date().toISOString());
    if (imageUrl) {
      const photo = el("img");
      photo.src = imageUrl;
      photo.alt = "";
      userBubble.prepend(photo);
    }
    addNode(userBubble);
    tracker.track("click", { trackId: "chatbot_message_sent" });
    const typing = el("div", "indexaai-widget-typing");
    typing.append(el("span", "indexaai-widget-sr", "Só um instante…"));
    for (let dot = 0; dot < 3; dot += 1) typing.append(el("span", "indexaai-widget-dot"));
    addNode(typing);

    let imageContext: AssistantImageContext | undefined;
    let reply: AssistantReply | null = null;
    let failure: AssistantFailure = "error";

    const found = image ? await searchByImage(apiBase, publicKey, image) : null;
    if (!image || found) {
      if (found) {
        imageContext = { caption: captionFrom(found.caption), documentIds: found.documentIds };
        userBubble.append(
          el("span", "indexaai-widget-caption", `A foto mostra: ${imageContext.caption}`),
        );
      }
      const result = await sendAssistantMessage(
        apiBase,
        publicKey,
        buildAssistantBody({
          message: message || (image ? IMAGE_TURN_TEXT : message),
          visitorId,
          pageUrl: location.href,
          product: getProduct(),
          sessionId: activeSessionId(),
          clientSessionId: isActive(session) ? clientSessionId : ensureClientSessionId(),
          image: imageContext,
          action: options.action,
        }),
      );
      if (result.ok) reply = result.reply;
      else failure = result.reason;
    }
    typing.remove();

    if (reply) {
      rememberSession(reply);
      quoteItems = reply.quoteItems;
      renderQuote();
      availableProducts = options.isLoadMore
        ? mergeProducts(availableProducts, reply.products)
        : options.action
          ? availableProducts
          : reply.products;
      hasMoreProductResults = reply.hasMoreProducts === true;
      renderProductSheet();
      addNode(bubble("bot", reply.answer, new Date().toISOString()));
      if (reply.products.length > 0)
        addNode(buildCards(reply.products, reply.hasMoreProducts === true));
      const followUp = buildFollowUpChips(reply.categories ?? []);
      if (followUp) addNode(followUp);
      requestLivePrices([
        ...availableProducts.map((product) => product.documentId),
        ...quoteItems.map((item) => item.documentId),
      ]);
    } else {
      userBubble.remove();
      if (options.fromForm) input.value = text;
      if (image && imageUrl) {
        pendingImage = image;
        pendingImageUrl = imageUrl;
        showAttachment(image, imageUrl);
      }
      addNode(bubble("error", ERROR_TEXT[failure], new Date().toISOString()));
    }

    setPending(false);
    renderProductSheet();
    input.focus();
  };

  // Abre/fecha com a animação do CSS. O fechamento só tira o painel do fluxo depois dela.
  const setOpen = (open: boolean): void => {
    if (open === isOpen) return;
    isOpen = open;
    if (closeTimer !== null) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    button.setAttribute("aria-expanded", String(open));
    if (open) {
      panel.classList.remove("is-closing");
      panel.hidden = false;
      if (!conversationRendered) {
        if (isActive(session)) void restoreCurrentSession();
        else renderWelcome();
      } else {
        messages.scrollTop = messages.scrollHeight;
      }
      input.focus();
    } else {
      closeSheet(false);
      panel.classList.add("is-closing");
      closeTimer = setTimeout(() => {
        panel.hidden = true;
        panel.classList.remove("is-closing");
        closeTimer = null;
      }, CLOSE_MS);
    }
    tracker.track("click", { trackId: open ? "chatbot_open" : "chatbot_close" });
  };

  // Conteúdo inicial: sessões salvas só são restauradas quando o visitante abre o widget.
  if (isActive(session)) {
    setView("chat");
    messages.replaceChildren();
    closeSheet(false);
    newConversationButton.hidden = false;
  } else {
    renderWelcome();
  }

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void sendTurn(input.value, { fromForm: true });
  });
  quoteButton.addEventListener("click", openQuoteSheet);
  sheetCloseButton.addEventListener("click", () => closeSheet());
  sheetOverlay.addEventListener("click", (event) => {
    if (event.target === sheetOverlay) closeSheet();
  });
  sheetOverlay.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeSheet();
    }
  });
  attachButton.addEventListener("click", () => fileInput.click());
  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (file) attachImage(file);
    fileInput.value = "";
  });
  attachRemove.addEventListener("click", () => {
    clearAttachment();
    status.textContent = "";
  });
  historyButton.addEventListener("click", () => {
    if (view === "chat") renderHistory();
    else void restoreCurrentSession();
  });
  newConversationButton.addEventListener("click", startNewConversation);
  feedbackButton.addEventListener("click", renderFeedback);
  button.addEventListener("click", () => setOpen(!isOpen));
  closeButton.addEventListener("click", () => {
    setOpen(false);
    button.focus();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !isOpen) return;
    setOpen(false);
    button.focus();
  });

  shadowRoot.append(root);
  document.body.append(host);
}
